/**
 * CutoffText
 * --------------------------------------------------
 *
 * Creates accessible expandable text previews
 * from Kirby Writer HTML.
 *
 * Rules:
 * - Single content element: never collapse
 * - Default preview: first element
 * - Short first paragraph: include second element
 * - First element is a heading: include second element
 * - Collapse only if additional elements remain
 *
 * Accessibility:
 * - Native button
 * - aria-expanded and aria-controls
 * - Hidden content removed from tab order and
 *   accessibility tree
 * - Full content visible without JavaScript
 * - No duplicated content
 */

class CutoffText {

    constructor() {
        this.dom = {};

        this.dom.items = document.querySelectorAll('[data-cutoff]');

        this.shortTextLimit = 120;
        this.instanceCount = 0;
    }

    /**
     * Get direct content elements.
     */
    getElements = (content) => {
        return Array.from(content.children);
    };

    /**
     * Count visible text characters.
     */
    getTextLength = (element) => {
        return (element.textContent || '')
            .replace(/\s+/g, ' ')
            .trim()
            .length;
    };

    /**
     * Determine number of elements in preview.
     */
    getPreviewCount = (elements) => {

        if (elements.length <= 1) {
            return elements.length;
        }

        const first = elements[0];

        const isHeading = /^H[1-6]$/.test(first.tagName);

        const isShortParagraph =
            first.tagName === 'P' &&
            this.getTextLength(first) <= this.shortTextLimit;

        return (isHeading || isShortParagraph) ? 2 : 1;
    };

    /**
     * Initialize individual cutoff.
     */
    prepareElement = (item) => {

        const content = item.querySelector('[data-cutoff-content]');

        // if (!content) return;
        if (!content) {
            item.classList.add('is-initialized');
            return;
        }

        const elements = this.getElements(content);
        const previewCount = this.getPreviewCount(elements);

        // No additional content: no cutoff needed.
        // if (elements.length <= previewCount) return;
        if (elements.length <= previewCount) {
            item.classList.add('is-initialized');
            return;
        }

        this.instanceCount++;

        const id = `cutoff-extra-${this.instanceCount}`;

        const preview = document.createElement('div');
        preview.className = 'cutoff__preview';

        const extra = document.createElement('div');
        extra.className = 'cutoff__extra';
        extra.id = id;
        extra.hidden = true;

        // Move original elements, never clone them.
        elements.forEach((element, index) => {
            if (index < previewCount) {
                preview.appendChild(element);
            } else {
                extra.appendChild(element);
            }
        });

        // Remove original content wrapper.
        content.replaceChildren(preview, extra);

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'cutoff__btn';

        button.setAttribute('aria-expanded', 'false');
        button.setAttribute('aria-controls', id);

        const label = document.createElement('span');
        label.className = 'cutoff__label';
        label.textContent = item.dataset.labelExpand || 'Mehr anzeigen';

        const icon = document.createElement('span');
        icon.className = 'cutoff__icon';
        icon.setAttribute('aria-hidden', 'true');

        icon.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg"
                 viewBox="0 0 24 24"
                 fill="none"
                 stroke="currentColor"
                 stroke-width="1.5"
                 stroke-linecap="round"
                 stroke-linejoin="round">
                <path d="m19.5 8.25-7.5 7.5-7.5-7.5"/>
            </svg>
        `;

        button.append(label, icon);

        content.after(button);

        button.addEventListener('click', () => {

            const expanded =
                button.getAttribute('aria-expanded') === 'true';

            const nextExpanded = !expanded;

            extra.hidden = !nextExpanded;

            button.setAttribute(
                'aria-expanded',
                String(nextExpanded)
            );

            label.textContent = nextExpanded
                ? item.dataset.labelCollapse || 'Weniger anzeigen'
                : item.dataset.labelExpand || 'Mehr anzeigen';

            item.classList.toggle('is-expanded', nextExpanded);

        });

        // item.classList.add('is-collapsible');
        item.classList.add('is-collapsible', 'is-initialized');
    };

    /**
     * Initialize all instances.
     */
    init = () => {

        if (!this.dom.items.length) return;

        this.dom.items.forEach((item) => {
            this.prepareElement(item);
        });

    };

}

export default CutoffText;
